import React from 'react'
import ReactMarkdown, { Components } from 'react-markdown'

interface SafeMarkdownProps {
  children: string
  className?: string
  /** Allow links to be rendered. Defaults to true. */
  allowLinks?: boolean
  /** Custom link className. Defaults to primary color with underline. */
  linkClassName?: string
}

/**
 * Safe markdown renderer that only allows a limited subset of markdown.
 * - Links: [text](url) - opens in new tab with noopener noreferrer
 * - Bold: **text** or __text__
 * - Italic: *text* or _text_
 * - Inline code: `text`
 *
 * Dangerous elements (script, iframe, img, etc.) are stripped.
 * This is safe for rendering user/admin-provided content.
 */
export const SafeMarkdown: React.FC<SafeMarkdownProps> = ({
  children,
  className,
  allowLinks = true,
  linkClassName = 'text-primary-600 underline hover:text-primary-700',
}) => {
  // Define safe component overrides
  const components: Components = {
    // Links open in new tab with security attributes
    a: ({ href, children }) => {
      if (!allowLinks) {
        return <>{children}</>
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
        >
          {children}
        </a>
      )
    },
    // Render paragraphs as spans to avoid block-level nesting issues
    p: ({ children }) => <span>{children}</span>,
    // Allow basic formatting
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    code: ({ children }) => (
      <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code>
    ),
    // Block dangerous/unwanted elements
    script: () => null,
    iframe: () => null,
    img: () => null,
    style: () => null,
    // Block block-level elements that could break layout
    h1: ({ children }) => <span className="font-bold">{children}</span>,
    h2: ({ children }) => <span className="font-bold">{children}</span>,
    h3: ({ children }) => <span className="font-bold">{children}</span>,
    h4: ({ children }) => <span className="font-bold">{children}</span>,
    h5: ({ children }) => <span className="font-bold">{children}</span>,
    h6: ({ children }) => <span className="font-bold">{children}</span>,
    blockquote: ({ children }) => <span>{children}</span>,
    ul: ({ children }) => <span>{children}</span>,
    ol: ({ children }) => <span>{children}</span>,
    li: ({ children }) => <span>{children} </span>,
    pre: ({ children }) => <span>{children}</span>,
    hr: () => null,
    table: () => null,
    thead: () => null,
    tbody: () => null,
    tr: () => null,
    th: () => null,
    td: () => null,
  }

  return (
    <span className={className}>
      <ReactMarkdown components={components}>
        {children}
      </ReactMarkdown>
    </span>
  )
}

export default SafeMarkdown
